import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { MUTATION_KEY, QUERY_KEY } from './keys';
import { queryClient } from './queryClient';
import { clmApiClient } from '../clients';
import type {
  ApiErrorResponse,
  IDN,
  IDNCreateRequest,
  IDNCreateRequestBody,
} from '../types';
import { isApiErrorResponse } from '../utils';

const updateIDN = async (data: IDNCreateRequest): Promise<IDN> => {
  const response = await clmApiClient.put<IDN, ApiErrorResponse>(
    `idn/${data.id}`,
    {
      idn: data,
    } as IDNCreateRequestBody,
  );

  if (!response.data) {
    throw new Error('No data returned from update IDN');
  }

  if (!response.ok) {
    if (isApiErrorResponse(response.data)) {
      throw new Error(response.data.details);
    }
    throw new Error('Unknown error');
  }

  await queryClient.invalidateQueries({
    predicate: (query) => {
      const [key, idnId] = query.queryKey as [string, string];

      if (key === QUERY_KEY.IDN_SEARCH) {
        return true;
      }
      if (key === QUERY_KEY.IDN_GET && idnId === data.id) {
        return true;
      }

      return false;
    },
  });

  return response.data;
};

export const useIDNUpdateMutation = (
  config?: Omit<UseMutationOptions<IDN, Error, IDNCreateRequest>, 'mutationFn'>,
) =>
  useMutation({
    mutationKey: [MUTATION_KEY.IDN_UPDATE],
    mutationFn: updateIDN,
    ...config,
  });
